/**
 * A rating, read-only. Filled and hollow stars rather than a number, with the
 * number in the accessible name so a screen reader hears "4 out of 5" instead of
 * five separate glyphs.
 */
export function Stars({ value, max = 5 }: { value: number; max?: number }) {
  return (
    <span
      className="tabular text-caption text-brass-text"
      role="img"
      aria-label={`${value}/${max}`}
    >
      {'★'.repeat(Math.max(0, Math.min(value, max)))}
      <span className="text-sage">{'☆'.repeat(Math.max(0, max - value))}</span>
    </span>
  );
}

/** The same shape, but clickable. Radio inputs so keyboard and voice both work. */
export function StarPicker({
  value,
  onChange,
  name,
  label,
  max = 5,
}: {
  value: number;
  onChange: (next: number) => void;
  name: string;
  label: string;
  max?: number;
}) {
  return (
    <fieldset className="flex flex-col gap-1 border-0 p-0">
      <legend className="text-caption font-medium text-ink">{label}</legend>
      <span className="flex items-center gap-1">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <label key={n} className="cursor-pointer">
            <input
              type="radio"
              name={name}
              value={n}
              checked={value === n}
              onChange={() => onChange(n)}
              className="sr-only"
            />
            <span
              aria-hidden="true"
              className={`text-[22px] leading-none ${n <= value ? 'text-brass-text' : 'text-sage'}`}
            >
              {n <= value ? '★' : '☆'}
            </span>
            <span className="sr-only">{`${n}/${max}`}</span>
          </label>
        ))}
      </span>
    </fieldset>
  );
}
